import React, { useEffect, useMemo, useState } from 'react';

import { Flex, Text, VStack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Button, Icon, Input, Select,
} from 'components';
import { debounceTableFilter } from 'utils/debounces';
import { parseQuerySearchToArray } from 'utils/parsers';

import GrayText from './GrayText';
import ModalSection from './Section';

function FilterBadge({ label = 'badge', removeFilter }) {
  return (
    <Flex
      w="fit-content"
      px={3}
      py={2}
      my={2}
      mr={1}
      borderRadius={100}
      align="center"
      justify="center"
      bg="secondary.default">
      <Text color="white">{label}</Text>
      <Icon
        pl={2}
        size="sm"
        family="fas"
        name="times"
        color="white"
        onClick={() => removeFilter({ label })}
        _hover={{ cursor: 'pointer' }}
      />
    </Flex>
  );
}

interface SelectFilterProps {
  options: any[]
}

export default function SelectFilter({
  options,
}: SelectFilterProps) {
  const form = useForm();
  const { search } = useLocation();
  const { location, push } = useHistory();

  const searchParams = useMemo(() => parseQuerySearchToArray(search), []);
  const [filters, setFilters] = useState(searchParams);

  const {
    handleSubmit, control, register, reset, setError,
  } = form;

  useEffect(() => {
    const [first, ...rest] = filters;
    let query = location.pathname;

    if (first) {
      query = query.concat(`?${first.key}=${first.value}`);
    }
    if (!isEmpty(rest)) {
      rest.forEach(({ key, value }) => {
        query = query.concat(`&${key}=${value}`);
      });
    }

    push(query);
  }, [filters]);

  function addFilter({ filter, value }) {
    if (!filter?.key) {
      setError('filter.key', {
        message: 'Esse campo é necessário para aplicar um filtro de busca',
        type: 'required',
      });
      return;
    }
    if (!value) {
      setError('value', {
        message: 'Esse campo é necessário para aplicar um filtro de busca',
        type: 'required',
      });
      return;
    }

    const reapeatedIndex = filters.findIndex(({ key }) => key === filter.key);

    if (reapeatedIndex >= 0) {
      const filtersCopy = filters;
      filtersCopy[reapeatedIndex] = { value, key: filter.key };
      setFilters([...filtersCopy]);
    } else {
      setFilters([...filters, { value, key: filter.key }]);
    }
    reset({ filter: undefined, search: null });
  }

  function removeFilter({ key }) {
    const newFilters = filters.filter((filter) => filter.key !== key);
    setFilters(newFilters);
  }

  return (
    <ModalSection>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(addFilter)}>
          <Flex
            flexDir="row"
            flexWrap="wrap">
            {filters.map(({ key, value }) => (
              <FilterBadge
                key={`${key}-${value}`}
                label={value}
                removeFilter={() => removeFilter({ key })}
              />
            ))}
          </Flex>
          <GrayText
            mt={filters.length < 1 ? 0 : 6}
            mb={2}
            variant="smallBold">
            Filtro
          </GrayText>
          <VStack width="100%">
            <Controller
              control={control}
              name="filter"
              render={({ field }) => (
                <Select
                  id="key"
                  defaultOptions={options}
                  loadOptions={(value, callback) => debounceTableFilter(value, callback, options)}
                  {...field}
                />
              )}
            />
            <Input
              h={12}
              {...register('value')}
              placeholder="Palavra-chave"
            />
          </VStack>
          <Button
            w="fit-content"
            type="submit"
            pl={0}
            pt={6}
            variant="ghost.blue">
            <Text
              variant="mediumBold"
              color="primary.default">+ Adicionar Filtro
            </Text>
          </Button>
        </form>
      </FormProvider>
    </ModalSection>
  );
}
